// Who a batch is assigned to, derived from the reviewer queues.
//
// Pure functions, separate from the view, so the rules are testable without
// rendering — the same reason batchFilter and batchGrouping are.

import type { AuditQueue } from "../types";

/** The per-reviewer queue id prefix the server writes (assignpolicy.QueuePrefix). */
export const REVIEWER_QUEUE_PREFIX = "rv-";

/**
 * The reviewer a per-person queue belongs to, or null for a shared one.
 *
 * A shared queue ("reviewers") is NOT a person and must never be rendered as
 * one: it has many members, and naming it as an owner would tell a reviewer a
 * batch is theirs when in fact everyone can see it.
 */
export function ownerOf(queue: AuditQueue): string | null {
  if (!queue.id.startsWith(REVIEWER_QUEUE_PREFIX)) return null;
  const id = queue.id.slice(REVIEWER_QUEUE_PREFIX.length);
  return id || null;
}

/** Where one batch sits: which queue, and whose it is. */
export interface BatchAssignment {
  queueId: string;
  queueName: string;
  /** The owning reviewer, for a per-person queue. Null for a shared queue. */
  ownerId: string | null;
  /**
   * The owner's display name, when the server resolved one. Null otherwise —
   * distinct from the id, so a caller can fall back deliberately rather than
   * rendering a uuid it believed was a name.
   */
  ownerName: string | null;
  /** Everyone who can see it — one person, or the shared queue's membership. */
  reviewerIds: string[];
  /**
   * Labels for reviewerIds, in the same order: the display name where the
   * server resolved one, the id where it did not. For listing an audience,
   * where an unresolved id is still more informative than omitting the person.
   */
  reviewerLabels: string[];
  /** True when more than one person can see it: a shared queue, or the
   *  transitional state where a batch sits in two queues at once. */
  shared: boolean;
}

/**
 * Display names by principal id, across every queue.
 *
 * Built once for the whole index rather than per batch: with a thousand-odd
 * batches over six reviewers, resolving per row would repeat the same six
 * lookups for every one of them.
 */
function nameIndex(queues: readonly AuditQueue[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const q of queues) {
    for (const r of q.reviewers ?? []) {
      if (r.id && r.displayName) out.set(r.id, r.displayName);
    }
  }
  return out;
}

/** The label for one principal: their name if known, else the raw id. */
function labelFor(names: Map<string, string>, id: string): string {
  return names.get(id) || id;
}

/**
 * Index the queues by batch id.
 *
 * A batch in MORE THAN ONE queue is recorded as shared rather than having one
 * of the two silently win. That state is reachable during the migration to
 * per-reviewer queues — some batches still sit in the old shared queue — and
 * `Scope()` unions across queues, so both sets of reviewers really can see it.
 * Showing one owner would be a claim the system does not make.
 */
export function indexAssignments(
  queues: readonly AuditQueue[],
): Map<string, BatchAssignment> {
  const names = nameIndex(queues);
  const out = new Map<string, BatchAssignment>();
  for (const q of queues) {
    const owner = ownerOf(q);
    for (const batchId of q.batchIds) {
      const existing = out.get(batchId);
      if (!existing) {
        out.set(batchId, {
          queueId: q.id,
          queueName: q.name || q.id,
          ownerId: owner,
          ownerName: owner ? (names.get(owner) ?? null) : null,
          reviewerIds: [...q.reviewerIds],
          reviewerLabels: q.reviewerIds.map((id) => labelFor(names, id)),
          shared: q.reviewerIds.length > 1,
        });
        continue;
      }
      // Second queue holding the same batch. Merge the audiences and stop
      // claiming a single owner.
      const merged = new Set([...existing.reviewerIds, ...q.reviewerIds]);
      const mergedIds = [...merged];
      out.set(batchId, {
        queueId: existing.queueId,
        queueName: existing.queueName,
        ownerId: null,
        ownerName: null,
        reviewerIds: mergedIds,
        reviewerLabels: mergedIds.map((id) => labelFor(names, id)),
        shared: true,
      });
    }
  }
  return out;
}

/** Just the assigned batch ids — what the batch filter needs. */
export function assignedBatchIds(queues: readonly AuditQueue[]): Set<string> {
  const out = new Set<string>();
  for (const q of queues) {
    for (const b of q.batchIds) out.add(b);
  }
  return out;
}

/**
 * How to label a batch's assignment in one short string.
 *
 * Returns null when nothing is known, so a caller renders nothing rather than
 * "Unassigned" — queues that failed to load and a genuinely unassigned batch
 * are different facts, and the second is actionable while the first is a bug.
 * Callers distinguish them by whether the queue list loaded at all.
 */
export function assignmentLabel(a: BatchAssignment | undefined): string | null {
  if (!a) return null;
  // Name first. The id is the fallback, not the answer — "assigned to
  // 01a01b6e-4537-7ea3-81d6-8a76cd0833eb" tells a reader nothing they came to
  // find out.
  if (a.ownerName) return a.ownerName;
  if (a.ownerId) return a.ownerId;
  if (a.reviewerIds.length === 1)
    return a.reviewerLabels[0] || a.reviewerIds[0];
  if (a.reviewerIds.length > 1)
    return `${a.queueName} (${a.reviewerIds.length})`;
  // A queue with batches and no members. Real, and worth surfacing: the work is
  // assigned somewhere nobody can reach, which looks exactly like having no
  // work at all.
  return `${a.queueName} — no members`;
}

/** Per-reviewer totals, for a summary of where the work sits. */
export function workloadByReviewer(
  queues: readonly AuditQueue[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (const q of queues) {
    const owner = ownerOf(q);
    if (owner) {
      out.set(owner, (out.get(owner) ?? 0) + q.batchIds.length);
      continue;
    }
    // Shared queue: every member can see every batch in it, so it counts for
    // each of them. Splitting it N ways would understate what each person
    // actually faces.
    for (const r of q.reviewerIds) {
      out.set(r, (out.get(r) ?? 0) + q.batchIds.length);
    }
  }
  return out;
}
